import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { X, User, GraduationCap, Phone, Building } from "lucide-react";
import {
  createTeacher,
  updateTeacher,
  getTeacherById,
} from "../../services/teachers";
import { getDepartments } from "../../services/departments";
import {
  CreateTeacherRequest,
  UpdateTeacherRequest,
} from "../../types/teacher";
import Input from "../common/Input";
import LoadingSpinner from "../common/LoadingSpinner";

interface TeacherModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId?: string; // If provided, this is an edit modal
}

const TeacherModal: React.FC<TeacherModalProps> = ({
  isOpen,
  onClose,
  teacherId,
}) => {
  const queryClient = useQueryClient();
  const isEditing = Boolean(teacherId);

  // Add validation errors state
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const [formData, setFormData] = useState({
    // Personal Information
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    address: "",

    // Professional Information
    department: "",
    designation: "",
    joiningDate: new Date().toISOString().split("T")[0],
    employmentType: "full_time" as const,

    // Qualifications
    educationDegree: "",
    educationInstitution: "",
    educationYear: "",
    educationGrade: "",
    educationMajor: "",

    // Certifications
    certificationName: "",
    certificationOrg: "",
    certificationIssueDate: "",
    certificationExpiryDate: "",

    // Experience
    totalYears: "",
    previousPosition: "",
    previousInstitution: "",
    previousStartDate: "",
    previousEndDate: "",

    // Salary Information
    basicSalary: "",
    paymentSchedule: "monthly" as const,

    // Emergency Contact
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelationship: "",
    emergencyContactEmail: "",
    emergencyContactAddress: "",

    // Bank Details
    accountHolderName: "",
    accountNumber: "",
    bankName: "",
    branchName: "",
    routingNumber: "",
  });

  // Fetch teacher data for editing
  const { data: teacherData, isLoading: isFetchingTeacher } = useQuery({
    queryKey: ["teacher", teacherId],
    queryFn: () => getTeacherById(teacherId!),
    enabled: !!teacherId && isOpen,
  });

  // Fetch departments for dropdown
  const { data: departmentsData, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ["departments-dropdown"],
    queryFn: () => getDepartments(1, 100, undefined, undefined, true), // Get all active departments
  });

  // Create teacher mutation
  const createMutation = useMutation({
    mutationFn: createTeacher,
    onSuccess: () => {
      toast.success("Teacher created successfully!");
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      queryClient.invalidateQueries({ queryKey: ["teacherStats"] });
      onClose();
      resetForm();
      setValidationErrors({}); // Clear validation errors on success
    },
    onError: (error: any) => {
      // Check if it's a validation error
      if (
        error?.response?.data?.success === false &&
        error?.response?.data?.errors
      ) {
        const errors: Record<string, string> = {};
        error.response.data.errors.forEach((err: any) => {
          errors[err.path] = err.msg;
        });
        setValidationErrors(errors);
        toast.error("Please fix the validation errors");
      } else {
        toast.error(
          error?.response?.data?.message || "Failed to create teacher",
        );
        setValidationErrors({}); // Clear validation errors for other errors
      }
    },
  });

  // Update teacher mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeacherRequest }) =>
      updateTeacher(id, data),
    onSuccess: () => {
      toast.success("Teacher updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["teachers"] });
      queryClient.invalidateQueries({ queryKey: ["teacherStats"] });
      queryClient.invalidateQueries({ queryKey: ["teacher", teacherId] });
      onClose();
      setValidationErrors({}); // Clear validation errors on success
    },
    onError: (error: any) => {
      // Check if it's a validation error
      if (
        error?.response?.data?.success === false &&
        error?.response?.data?.errors
      ) {
        const errors: Record<string, string> = {};
        error.response.data.errors.forEach((err: any) => {
          errors[err.path] = err.msg;
        });
        setValidationErrors(errors);
        toast.error("Please fix the validation errors");
      } else {
        toast.error(
          error?.response?.data?.message || "Failed to update teacher",
        );
        setValidationErrors({}); // Clear validation errors for other errors
      }
    },
  });

  // Load teacher data when editing
  useEffect(() => {
    if (isEditing && teacherData?.data) {
      const teacher = teacherData.data;
      // Get user info from populated userId field
      const userInfo =
        teacher.userInfo ||
        (typeof teacher.userId === "object" ? teacher.userId : null);

      setFormData({
        // Personal Information
        firstName: userInfo?.firstName || "",
        lastName: userInfo?.lastName || "",
        email: userInfo?.email || "",
        password: "", // Don't populate password for security
        phone: userInfo?.profileDetails?.phone || "",
        dateOfBirth: userInfo?.profileDetails?.dateOfBirth || "",
        gender: userInfo?.profileDetails?.gender || "",
        address:
          typeof userInfo?.profileDetails?.address === "string"
            ? userInfo.profileDetails.address
            : userInfo?.profileDetails?.address?.street || "",

        // Professional Information
        department: teacher.department || "",
        designation: teacher.designation || "",
        joiningDate:
          teacher.joiningDate?.split("T")[0] ||
          new Date().toISOString().split("T")[0],
        employmentType: (teacher.employmentType as any) || "full_time",

        // Qualifications
        educationDegree: teacher.qualification?.education?.[0]?.degree || "",
        educationInstitution:
          teacher.qualification?.education?.[0]?.institution || "",
        educationYear:
          teacher.qualification?.education?.[0]?.year?.toString() || "",
        educationGrade: teacher.qualification?.education?.[0]?.grade || "",
        educationMajor: teacher.qualification?.education?.[0]?.major || "",

        // Certifications
        certificationName:
          teacher.qualification?.certifications?.[0]?.name || "",
        certificationOrg:
          teacher.qualification?.certifications?.[0]?.issuingOrganization || "",
        certificationIssueDate:
          teacher.qualification?.certifications?.[0]?.issueDate || "",
        certificationExpiryDate:
          teacher.qualification?.certifications?.[0]?.expiryDate || "",

        // Experience
        totalYears:
          teacher.qualification?.experience?.totalYears?.toString() || "",
        previousPosition:
          teacher.qualification?.experience?.previousPositions?.[0]?.position ||
          "",
        previousInstitution:
          teacher.qualification?.experience?.previousPositions?.[0]
            ?.institution || "",
        previousStartDate:
          teacher.qualification?.experience?.previousPositions?.[0]
            ?.startDate || "",
        previousEndDate:
          teacher.qualification?.experience?.previousPositions?.[0]?.endDate ||
          "",

        // Salary Information
        basicSalary: teacher.salary?.basicSalary?.toString() || "",
        paymentSchedule: (teacher.salary?.paymentSchedule as any) || "monthly",

        // Emergency Contact
        emergencyContactName: teacher.emergencyContact?.name || "",
        emergencyContactPhone: teacher.emergencyContact?.phone || "",
        emergencyContactRelationship:
          teacher.emergencyContact?.relationship || "",
        emergencyContactEmail: teacher.emergencyContact?.email || "",
        emergencyContactAddress: teacher.emergencyContact?.address || "",

        // Bank Details
        accountHolderName: teacher.bankDetails?.accountHolderName || "",
        accountNumber: teacher.bankDetails?.accountNumber || "",
        bankName: teacher.bankDetails?.bankName || "",
        branchName: teacher.bankDetails?.branchName || "",
        routingNumber: teacher.bankDetails?.routingNumber || "",
      });
    }
  }, [teacherData, isEditing]);

  const resetForm = () => {
    setFormData({
      // Personal Information
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phone: "",
      dateOfBirth: "",
      gender: "",
      address: "",

      // Professional Information
      department: "",
      designation: "",
      joiningDate: new Date().toISOString().split("T")[0],
      employmentType: "full_time",

      // Qualifications
      educationDegree: "",
      educationInstitution: "",
      educationYear: "",
      educationGrade: "",
      educationMajor: "",

      // Certifications
      certificationName: "",
      certificationOrg: "",
      certificationIssueDate: "",
      certificationExpiryDate: "",

      // Experience
      totalYears: "",
      previousPosition: "",
      previousInstitution: "",
      previousStartDate: "",
      previousEndDate: "",

      // Salary Information
      basicSalary: "",
      paymentSchedule: "monthly",

      // Emergency Contact
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyContactRelationship: "",
      emergencyContactEmail: "",
      emergencyContactAddress: "",

      // Bank Details
      accountHolderName: "",
      accountNumber: "",
      bankName: "",
      branchName: "",
      routingNumber: "",
    });
    setValidationErrors({});
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Helper function to get error message for a field
  const getFieldError = (fieldPath: string) => {
    return validationErrors[fieldPath];
  };

  // Helper function to check if field has error
  const hasFieldError = (fieldPath: string) => {
    return !!validationErrors[fieldPath];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous validation errors
    setValidationErrors({});

    // Transform form data to API format
    const apiData: CreateTeacherRequest = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,

      // Profile Details
      profileDetails: {
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        address: formData.address,
      },

      // Professional Information
      department: formData.department,
      designation: formData.designation,
      joiningDate: formData.joiningDate,
      employmentType: formData.employmentType,

      // Qualifications
      qualification: {
        education: formData.educationDegree
          ? [
              {
                degree: formData.educationDegree,
                institution: formData.educationInstitution,
                year:
                  parseInt(formData.educationYear) || new Date().getFullYear(),
                grade: formData.educationGrade,
                major: formData.educationMajor,
              },
            ]
          : [],

        certifications: formData.certificationName
          ? [
              {
                name: formData.certificationName,
                issuingOrganization: formData.certificationOrg,
                issueDate: formData.certificationIssueDate,
                expiryDate: formData.certificationExpiryDate,
              },
            ]
          : [],

        experience: {
          totalYears: parseInt(formData.totalYears) || 0,
          previousPositions: formData.previousPosition
            ? [
                {
                  position: formData.previousPosition,
                  institution: formData.previousInstitution,
                  startDate: formData.previousStartDate,
                  endDate: formData.previousEndDate,
                  responsibilities: [],
                },
              ]
            : [],
        },
      },

      // Salary Information
      salary: formData.basicSalary
        ? {
            basicSalary: parseFloat(formData.basicSalary),
            paymentSchedule: formData.paymentSchedule,
            allowances: [],
            deductions: [],
          }
        : undefined,

      // Emergency Contact
      emergencyContact: {
        name: formData.emergencyContactName,
        phone: formData.emergencyContactPhone,
        relationship: formData.emergencyContactRelationship,
        email: formData.emergencyContactEmail,
        address: formData.emergencyContactAddress,
      },

      // Bank Details
      bankDetails: formData.accountHolderName
        ? {
            accountHolderName: formData.accountHolderName,
            accountNumber: formData.accountNumber,
            bankName: formData.bankName,
            branchName: formData.branchName,
            routingNumber: formData.routingNumber,
          }
        : undefined,
    };

    if (isEditing && teacherId) {
      // For updates, we don't send password if it's empty
      if (!formData.password) {
        delete (apiData as any).password;
      }

      // For updates, separate user profile data from teacher data
      const teacherUpdateData = {
        // Professional Information
        department: formData.department,
        designation: formData.designation,
        joiningDate: formData.joiningDate,
        employmentType: formData.employmentType,

        // Complex objects
        qualification: apiData.qualification,
        salary: apiData.salary,
        emergencyContact: apiData.emergencyContact,
        bankDetails: apiData.bankDetails,
      };

      // User profile data (sent separately to backend)
      const userProfileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        address: formData.address,
      };

      // Combine the data for backend
      const updatePayload = {
        ...teacherUpdateData,
        userProfile: userProfileData,
      };

      updateMutation.mutate({ id: teacherId, data: updatePayload });
    } else {
      createMutation.mutate(apiData);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      onClose();
      if (!isEditing) {
        resetForm();
      }
    }
  };

  if (!isOpen) return null;

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? "Edit Teacher" : "Add New Teacher"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Required Fields Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <span className="text-red-500">*</span> indicates required fields
            </p>
          </div>

          {isFetchingTeacher && isEditing && (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          )}

          {(!isFetchingTeacher || !isEditing) && (
            <>
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="First Name *"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    error={validationErrors.firstName}
                  />

                  <Input
                    label="Last Name *"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    error={validationErrors.lastName}
                  />

                  <Input
                    label="Email *"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    error={validationErrors.email}
                  />

                  <Input
                    label={
                      isEditing
                        ? "New Password (leave blank to keep current)"
                        : "Password *"
                    }
                    name="password"
                    type="password"
                    required={!isEditing}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter password"
                    error={validationErrors.password}
                  />

                  <Input
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    error={validationErrors.phone}
                  />

                  <Input
                    label="Date of Birth"
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                  />

                  <div>
                    <label
                      htmlFor="gender"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Gender
                    </label>
                    <select
                      name="gender"
                      id="gender"
                      required
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <Input
                    label="Address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                    error={validationErrors.address}
                  />
                </div>
              </div>

              {/* Professional Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Professional Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="department"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Department *
                    </label>
                    <select
                      name="department"
                      id="department"
                      required
                      value={formData.department}
                      onChange={handleInputChange}
                      disabled={isLoadingDepartments}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">
                        {isLoadingDepartments
                          ? "Loading departments..."
                          : "Select department"}
                      </option>
                      {departmentsData?.data?.map((department) => (
                        <option key={department._id} value={department._id}>
                          {department.name} ({department.code})
                        </option>
                      ))}
                    </select>
                    {validationErrors.department && (
                      <p className="mt-1 text-sm text-red-600">
                        {validationErrors.department}
                      </p>
                    )}
                  </div>

                  <Input
                    label="Designation *"
                    name="designation"
                    type="text"
                    required
                    value={formData.designation}
                    onChange={handleInputChange}
                    placeholder="e.g., Professor, Assistant Professor"
                  />

                  <Input
                    label="Joining Date *"
                    name="joiningDate"
                    type="date"
                    required
                    value={formData.joiningDate}
                    onChange={handleInputChange}
                  />

                  <div>
                    <label
                      htmlFor="employmentType"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Employment Type *
                    </label>
                    <select
                      name="employmentType"
                      id="employmentType"
                      value={formData.employmentType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="full_time">Full Time</option>
                      <option value="part_time">Part Time</option>
                      <option value="contract">Contract</option>
                      <option value="substitute">Substitute</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Education & Qualifications */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Education & Qualifications
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Highest Degree"
                    name="educationDegree"
                    type="text"
                    value={formData.educationDegree}
                    onChange={handleInputChange}
                    placeholder="e.g., Master's, PhD"
                  />

                  <Input
                    label="Institution"
                    name="educationInstitution"
                    type="text"
                    value={formData.educationInstitution}
                    onChange={handleInputChange}
                    placeholder="University/College name"
                  />

                  <Input
                    label="Graduation Year"
                    name="educationYear"
                    type="number"
                    value={formData.educationYear}
                    onChange={handleInputChange}
                    placeholder="e.g., 2020"
                  />

                  <Input
                    label="Grade/CGPA"
                    name="educationGrade"
                    type="text"
                    value={formData.educationGrade}
                    onChange={handleInputChange}
                    placeholder="e.g., 3.8, First Class"
                  />

                  <Input
                    label="Major/Specialization"
                    name="educationMajor"
                    type="text"
                    value={formData.educationMajor}
                    onChange={handleInputChange}
                    placeholder="Field of study"
                  />

                  <Input
                    label="Years of Experience"
                    name="totalYears"
                    type="number"
                    value={formData.totalYears}
                    onChange={handleInputChange}
                    placeholder="Total years"
                  />
                </div>
              </div>

              {/* Professional Certifications */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Professional Certifications
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Certification Name"
                    name="certificationName"
                    type="text"
                    value={formData.certificationName}
                    onChange={handleInputChange}
                    placeholder="e.g., Teaching License"
                  />

                  <Input
                    label="Issuing Organization"
                    name="certificationOrg"
                    type="text"
                    value={formData.certificationOrg}
                    onChange={handleInputChange}
                    placeholder="Organization name"
                  />

                  <Input
                    label="Issue Date"
                    name="certificationIssueDate"
                    type="date"
                    value={formData.certificationIssueDate}
                    onChange={handleInputChange}
                  />

                  <Input
                    label="Expiry Date"
                    name="certificationExpiryDate"
                    type="date"
                    value={formData.certificationExpiryDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Previous Experience */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Previous Experience
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Previous Position"
                    name="previousPosition"
                    type="text"
                    value={formData.previousPosition}
                    onChange={handleInputChange}
                    placeholder="Job title"
                  />

                  <Input
                    label="Previous Institution"
                    name="previousInstitution"
                    type="text"
                    value={formData.previousInstitution}
                    onChange={handleInputChange}
                    placeholder="Company/School name"
                  />

                  <Input
                    label="Start Date"
                    name="previousStartDate"
                    type="date"
                    value={formData.previousStartDate}
                    onChange={handleInputChange}
                  />

                  <Input
                    label="End Date"
                    name="previousEndDate"
                    type="date"
                    value={formData.previousEndDate}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Salary Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Salary Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Basic Salary"
                    name="basicSalary"
                    type="number"
                    value={formData.basicSalary}
                    onChange={handleInputChange}
                    placeholder="Enter amount"
                  />

                  <div>
                    <label
                      htmlFor="paymentSchedule"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Payment Schedule
                    </label>
                    <select
                      name="paymentSchedule"
                      id="paymentSchedule"
                      value={formData.paymentSchedule}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Emergency Contact
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Contact Name *"
                    name="emergencyContactName"
                    type="text"
                    required
                    value={formData.emergencyContactName}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    error={getFieldError("emergencyContact.name")}
                  />

                  <Input
                    label="Phone Number *"
                    name="emergencyContactPhone"
                    type="tel"
                    required
                    value={formData.emergencyContactPhone}
                    onChange={handleInputChange}
                    placeholder="Phone number"
                    error={getFieldError("emergencyContact.phone")}
                  />

                  <Input
                    label="Relationship *"
                    name="emergencyContactRelationship"
                    type="text"
                    required
                    value={formData.emergencyContactRelationship}
                    onChange={handleInputChange}
                    placeholder="e.g., Spouse, Parent"
                  />

                  <Input
                    label="Email"
                    name="emergencyContactEmail"
                    type="email"
                    value={formData.emergencyContactEmail}
                    onChange={handleInputChange}
                    placeholder="Email address"
                    error={validationErrors["emergencyContact.email"]}
                  />

                  <Input
                    label="Address"
                    name="emergencyContactAddress"
                    type="text"
                    value={formData.emergencyContactAddress}
                    onChange={handleInputChange}
                    placeholder="Contact address"
                    error={validationErrors["emergencyContact.address"]}
                  />
                </div>
              </div>

              {/* Bank Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Bank Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Account Holder Name"
                    name="accountHolderName"
                    type="text"
                    value={formData.accountHolderName}
                    onChange={handleInputChange}
                    placeholder="As per bank records"
                  />

                  <Input
                    label="Account Number"
                    name="accountNumber"
                    type="text"
                    value={formData.accountNumber}
                    onChange={handleInputChange}
                    placeholder="Bank account number"
                  />

                  <Input
                    label="Bank Name"
                    name="bankName"
                    type="text"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    placeholder="Name of the bank"
                  />

                  <Input
                    label="Branch Name"
                    name="branchName"
                    type="text"
                    value={formData.branchName}
                    onChange={handleInputChange}
                    placeholder="Bank branch"
                  />

                  <Input
                    label="Routing Number/IFSC"
                    name="routingNumber"
                    type="text"
                    value={formData.routingNumber}
                    onChange={handleInputChange}
                    placeholder="Routing/IFSC code"
                  />
                </div>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (isFetchingTeacher && isEditing)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {isSubmitting && <LoadingSpinner />}
              {isEditing ? "Update Teacher" : "Create Teacher"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherModal;
